import { flow } from 'fp-ts/function';
import { useMemo } from 'react';
import { useDateTimeLocalizationInfo, useTimezone } from '@mews-framework/globalization';
import { createDateFormatPattern, TimeService } from '@optimus-web/core';
import { FieldAdapter } from '../hooks';
import {
    createDateFormatPatternAdapter,
    createIsoAdapter,
    createIsoTimeAdapter,
    createUtcAdapter,
    nullAdapter,
} from './adapters';

export const useDateFormatPattern = () => {
    const localizationInfo = useDateTimeLocalizationInfo();
    return createDateFormatPattern(localizationInfo.dateFormat);
};

const useDateFormatPatternAdapter = () => {
    const localizationInfo = useDateTimeLocalizationInfo();
    const dateFormatPattern = useDateFormatPattern();

    return useMemo(
        () => createDateFormatPatternAdapter(localizationInfo, dateFormatPattern),
        [dateFormatPattern, localizationInfo]
    );
};

export type DateFieldMode = 'utc' | 'iso' | 'localized';
export const useDateFieldTransform = (mode: DateFieldMode) => {
    const dateFormatPatternAdapter = useDateFormatPatternAdapter();
    const localizationInfo = useDateTimeLocalizationInfo();
    const timezone = useTimezone();

    return useMemo<FieldAdapter>(() => {
        if (mode === 'utc') {
            const utcAdapter = createUtcAdapter(localizationInfo, timezone);
            return { input: utcAdapter.input, output: flow(utcAdapter.output, dateFormatPatternAdapter.output) };
        }
        if (mode === 'iso') {
            const isoAdapter = createIsoAdapter(localizationInfo, timezone);
            return { input: isoAdapter.input, output: flow(isoAdapter.output, dateFormatPatternAdapter.output) };
        }
        return dateFormatPatternAdapter;
    }, [dateFormatPatternAdapter, localizationInfo, mode, timezone]);
};

interface TimeFieldTransformArgs {
    mode: 'iso' | 'localized';
    showSeconds?: boolean;
    timeService: TimeService;
}
export const useTimeFieldTransform = ({ mode, showSeconds, timeService }: TimeFieldTransformArgs) => {
    const localizationInfo = useDateTimeLocalizationInfo();

    return useMemo<FieldAdapter<string>>(
        () => (mode === 'iso' ? createIsoTimeAdapter({ localizationInfo, showSeconds, timeService }) : nullAdapter),
        [localizationInfo, mode, showSeconds, timeService]
    );
};

export type MonthFieldMode = 'utc' | 'iso' | 'localized';
export const useMonthFieldTransform = (mode: MonthFieldMode) => {
    const localizationInfo = useDateTimeLocalizationInfo();
    const timezone = useTimezone();

    return useMemo(() => {
        if (mode === 'utc') {
            return createUtcAdapter(localizationInfo, timezone);
        }
        if (mode === 'iso') {
            return createIsoAdapter(localizationInfo, timezone);
        }
    }, [localizationInfo, mode, timezone]);
};
